import React, { useState, useEffect } from 'react';
import { localInventoryAPI, warehousesAPI } from '@/lib/api';
import { formatDateTime as formatDateTimeUtil } from '@/lib/utils';
import { 
  ClipboardList, Calendar, MapPin, User, Package, 
  Search, Filter, RefreshCw, Eye, Zap, AlertTriangle, Link2, Copy
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';

const AdminLocalInventoriesPage = () => {
  const [inventories, setInventories] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [selectedWarehouse, setSelectedWarehouse] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewDialog, setViewDialog] = useState({ open: false, inventory: null });
  const [locationsWithoutWarehouse, setLocationsWithoutWarehouse] = useState([]);
  const [assignDialog, setAssignDialog] = useState({ open: false, location: null, selectedWarehouse: '' });

  useEffect(() => {
    loadData();
    checkLocationsWithoutWarehouse();
  }, [selectedWarehouse]);

  const checkLocationsWithoutWarehouse = async () => {
    try {
      const res = await localInventoryAPI.debugLocationsWarehouses();
      setLocationsWithoutWarehouse(res.data?.locations_without_warehouse || []);
    } catch (error) {
      console.error('Error verificando locales:', error);
    }
  };

  const assignWarehouse = async () => {
    if (!assignDialog.location || !assignDialog.selectedWarehouse) return;
    
    setSyncing(true);
    try {
      await localInventoryAPI.assignWarehouseToLocation(
        assignDialog.location.id, 
        assignDialog.selectedWarehouse
      );
      toast.success(`Bodega asignada correctamente a ${assignDialog.location.name}`);
      setAssignDialog({ open: false, location: null, selectedWarehouse: '' });
      checkLocationsWithoutWarehouse();
      loadData();
    } catch (error) {
      console.error('Error asignando bodega:', error);
      toast.error(error.response?.data?.detail || 'Error al asignar bodega');
    } finally {
      setSyncing(false);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      // Cargar bodegas
      const whRes = await warehousesAPI.getActive();
      setWarehouses(whRes.data || []);

      // Cargar inventarios
      const params = {};
      if (selectedWarehouse && selectedWarehouse !== 'all') {
        params.warehouse_id = selectedWarehouse;
      }
      
      const invRes = await localInventoryAPI.getAllForAdmin(params);
      setInventories(invRes.data?.inventories || []);
    } catch (error) {
      console.error('Error cargando datos:', error);
      toast.error('Error al cargar inventarios');
    } finally {
      setLoading(false);
    }
  };

  const applyInventoryToStock = async (inventoryId) => {
    setSyncing(true);
    try {
      const res = await localInventoryAPI.applyToStock(inventoryId);
      toast.success(res.data?.message || 'Inventario aplicado al stock');
      loadData();
    } catch (error) {
      console.error('Error aplicando inventario:', error);
      toast.error(error.response?.data?.detail || 'Error al aplicar inventario');
    } finally {
      setSyncing(false);
    }
  };

  const applyAllPending = async () => {
    setSyncing(true);
    try {
      const params = selectedWarehouse !== 'all' ? { warehouse_id: selectedWarehouse } : {};
      const res = await localInventoryAPI.applyAllPending(params);
      const data = res.data;
      toast.success(`Sincronización completada: ${data.success} inventarios aplicados, ${data.failed} fallidos`);
      loadData();
    } catch (error) {
      console.error('Error sincronizando:', error);
      toast.error(error.response?.data?.detail || 'Error al sincronizar inventarios');
    } finally {
      setSyncing(false);
    }
  };

  const filteredInventories = inventories.filter(inv => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      inv.location_name?.toLowerCase().includes(term) ||
      inv.created_by_name?.toLowerCase().includes(term) ||
      inv.warehouse_name?.toLowerCase().includes(term)
    );
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return dateStr;
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return formatDateTimeUtil(dateStr);
  };

  // Función para generar texto formateado para WhatsApp
  const generateWhatsAppText = (inventory) => {
    if (!inventory || !inventory.items || inventory.items.length === 0) {
      return null;
    }

    // Obtener fecha y hora actual en zona horaria de Chile
    const now = new Date();
    const chileDate = now.toLocaleDateString('es-CL', { 
      timeZone: 'America/Santiago',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
    const chileTime = now.toLocaleTimeString('es-CL', { 
      timeZone: 'America/Santiago',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });

    // Agrupar productos por proveedor
    const groupedBySupplier = {};
    const noSupplier = [];

    inventory.items.forEach(item => {
      const supplier = item.supplier || null;
      if (supplier) {
        if (!groupedBySupplier[supplier]) {
          groupedBySupplier[supplier] = [];
        }
        groupedBySupplier[supplier].push(item);
      } else {
        noSupplier.push(item);
      }
    });

    // Ordenar proveedores alfabéticamente
    const sortedSuppliers = Object.keys(groupedBySupplier).sort((a, b) => 
      a.localeCompare(b, 'es', { sensitivity: 'base' })
    );

    // Ordenar productos dentro de cada proveedor alfabéticamente
    sortedSuppliers.forEach(supplier => {
      groupedBySupplier[supplier].sort((a, b) => 
        (a.item_name || '').localeCompare(b.item_name || '', 'es', { sensitivity: 'base' })
      );
    });

    // Ordenar productos sin proveedor alfabéticamente
    noSupplier.sort((a, b) => 
      (a.item_name || '').localeCompare(b.item_name || '', 'es', { sensitivity: 'base' })
    );

    // Construir el texto
    let text = `INVENTARIO ACTUAL — ${inventory.location_name?.toUpperCase() || 'LOCAL'}\n`;
    text += `${chileDate} - ${chileTime} hrs\n`;

    // Agregar productos agrupados por proveedor
    sortedSuppliers.forEach(supplier => {
      text += `\n*${supplier.toUpperCase()}*\n`;
      groupedBySupplier[supplier].forEach(item => {
        const qty = Math.floor(item.quantity); // Mostrar como entero
        text += `${qty} ${item.item_name}\n`;
      });
    });

    // Agregar productos sin proveedor al final
    if (noSupplier.length > 0) {
      text += `\n*SIN PROVEEDOR*\n`;
      noSupplier.forEach(item => {
        const qty = Math.floor(item.quantity);
        text += `${qty} ${item.item_name}\n`;
      });
    }

    return text.trim();
  };

  // Función para copiar al portapapeles
  const copyToWhatsApp = async () => {
    const inventory = viewDialog.inventory;
    
    if (!inventory || !inventory.items || inventory.items.length === 0) {
      toast.error('No hay productos disponibles para copiar.');
      return;
    }

    const text = generateWhatsAppText(inventory);
    
    if (!text) {
      toast.error('No hay productos disponibles para copiar.');
      return;
    }

    try {
      await navigator.clipboard.writeText(text);
      toast.success('Inventario copiado para WhatsApp');
    } catch (error) {
      console.error('Error copiando al portapapeles:', error);
      toast.error('Error al copiar. Intenta de nuevo.');
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <ClipboardList className="w-7 h-7 text-orange-500" />
            Conteos de Inventario
          </h1>
          <p className="text-slate-500 mt-1">
            Auditorías físicas realizadas por los inventaristas de cada local
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={applyAllPending} 
            variant="default" 
            disabled={syncing}
            className="bg-green-600 hover:bg-green-700"
          >
            <Zap className={`w-4 h-4 mr-2 ${syncing ? 'animate-pulse' : ''}`} />
            {syncing ? 'Sincronizando...' : 'Sincronizar Stock'}
          </Button>
          <Button onClick={loadData} variant="outline" disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
        </div>
      </div>

      {/* Info de sincronización */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <p className="text-amber-800 text-sm">
          <strong>💡 Sincronizar Stock:</strong> Este botón aplica los inventarios de los inventaristas al stock de productos. 
          Úsalo para sincronizar inventarios que fueron realizados antes de la última actualización del sistema.
        </p>
      </div>

      {/* Alerta de locales sin bodega */}
      {locationsWithoutWarehouse.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5" />
            <div className="flex-1">
              <p className="text-red-800 font-semibold">
                ⚠️ {locationsWithoutWarehouse.length} local(es) sin bodega asignada
              </p>
              <p className="text-red-700 text-sm mt-1">
                Los inventarios de estos locales no aparecerán en los filtros por bodega. Asígnales una bodega:
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {locationsWithoutWarehouse.map(loc => (
                  <Button
                    key={loc.id}
                    variant="outline"
                    size="sm"
                    className="border-red-300 text-red-700 hover:bg-red-100"
                    onClick={() => setAssignDialog({ open: true, location: loc, selectedWarehouse: '' })}
                  >
                    <Link2 className="w-3 h-3 mr-1" />
                    {loc.name} ({loc.inventories_count || 0} inv.)
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filtros */}
      <div className="bg-white rounded-lg border border-slate-200 p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Buscar por local, usuario o bodega..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={selectedWarehouse} onValueChange={setSelectedWarehouse}>
            <SelectTrigger className="w-full md:w-[250px]">
              <Filter className="w-4 h-4 mr-2 text-slate-400" />
              <SelectValue placeholder="Filtrar por bodega" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las bodegas</SelectItem>
              {warehouses.map(wh => (
                <SelectItem key={wh.id} value={wh.id}>{wh.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Tabla de inventarios */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-slate-500">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2" />
            Cargando inventarios...
          </div>
        ) : filteredInventories.length === 0 ? (
          <div className="p-8 text-center text-slate-500">
            <ClipboardList className="w-12 h-12 mx-auto mb-2 opacity-50" />
            No se encontraron inventarios
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Fecha y Hora</th>
                  <th className="px-4 py-3 text-left font-semibold">Local</th>
                  <th className="px-4 py-3 text-left font-semibold">Bodega</th>
                  <th className="px-4 py-3 text-left font-semibold">Usuario</th>
                  <th className="px-4 py-3 text-center font-semibold">Productos</th>
                  <th className="px-4 py-3 text-center font-semibold">Unidades</th>
                  <th className="px-4 py-3 text-center font-semibold">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredInventories.map((inv) => (
                  <tr key={inv.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-slate-400" />
                        <div>
                          <div className="font-medium">{formatDate(inv.inventory_date)}</div>
                          <div className="text-sm text-slate-500">{inv.inventory_time}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-orange-500" />
                        <span className="font-medium">{inv.location_name || '-'}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-slate-600">{inv.warehouse_name || inv.warehouse_id?.slice(0, 8) || '-'}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-slate-400" />
                        <span>{inv.created_by_name || '-'}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
                        <Package className="w-3 h-3" />
                        {inv.total_items}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="font-semibold text-slate-700">{inv.total_quantity}</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setViewDialog({ open: true, inventory: inv })}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Ver
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Dialog para ver detalle */}
      <Dialog open={viewDialog.open} onOpenChange={(open) => setViewDialog({ open, inventory: null })}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-orange-500" />
              Detalle de Inventario
            </DialogTitle>
          </DialogHeader>
          
          {viewDialog.inventory && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <span className="text-sm text-slate-500">Local</span>
                  <p className="font-medium">{viewDialog.inventory.location_name}</p>
                </div>
                <div>
                  <span className="text-sm text-slate-500">Bodega</span>
                  <p className="font-medium">{viewDialog.inventory.warehouse_name || '-'}</p>
                </div>
                <div>
                  <span className="text-sm text-slate-500">Fecha</span>
                  <p className="font-medium">{viewDialog.inventory.inventory_date} {viewDialog.inventory.inventory_time}</p>
                </div>
                <div>
                  <span className="text-sm text-slate-500">Realizado por</span>
                  <p className="font-medium">{viewDialog.inventory.created_by_name}</p>
                </div>
              </div>

              <div>
                <h4 className="font-semibold mb-2">Productos ({viewDialog.inventory.total_items})</h4>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="px-3 py-2 text-left">Código</th>
                        <th className="px-3 py-2 text-left">Producto</th>
                        <th className="px-3 py-2 text-left">Proveedor</th>
                        <th className="px-3 py-2 text-right">Cantidad</th>
                        <th className="px-3 py-2 text-left">Unidad</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {viewDialog.inventory.items?.map((item, idx) => (
                        <tr key={idx} className="hover:bg-slate-50">
                          <td className="px-3 py-2 font-mono text-xs">{item.item_code}</td>
                          <td className="px-3 py-2">{item.item_name}</td>
                          <td className="px-3 py-2 text-slate-600 text-xs">{item.supplier || <span className="text-slate-400 italic">Sin proveedor</span>}</td>
                          <td className="px-3 py-2 text-right font-semibold">{item.quantity}</td>
                          <td className="px-3 py-2 text-slate-500">{item.unit}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {viewDialog.inventory.notes && (
                <div>
                  <span className="text-sm text-slate-500">Notas</span>
                  <p className="mt-1 p-2 bg-slate-50 rounded">{viewDialog.inventory.notes}</p>
                </div>
              )}

              <div className="flex items-center justify-between pt-2 border-t border-slate-200">
                <div className="text-sm text-slate-500">
                  Total unidades: <span className="font-bold text-slate-700">{viewDialog.inventory.total_quantity}</span>
                </div>
                <Button
                  onClick={copyToWhatsApp}
                  variant="outline"
                  size="sm"
                  className="gap-2 text-green-700 border-green-300 hover:bg-green-50 hover:border-green-400"
                  data-testid="copy-whatsapp-btn"
                >
                  <Copy className="w-4 h-4" />
                  Copiar para WhatsApp
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog para asignar bodega a local */}
      <Dialog open={assignDialog.open} onOpenChange={(open) => !open && setAssignDialog({ open: false, location: null, selectedWarehouse: '' })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="w-5 h-5 text-orange-500" />
              Asignar Bodega a Local
            </DialogTitle>
          </DialogHeader>
          
          {assignDialog.location && (
            <div className="space-y-4">
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="font-medium">{assignDialog.location.name}</p>
                <p className="text-sm text-slate-500">Código: {assignDialog.location.code}</p>
                {assignDialog.location.inventories_count > 0 && (
                  <p className="text-sm text-orange-600 mt-1">
                    Este local tiene {assignDialog.location.inventories_count} inventarios sin bodega asignada
                  </p>
                )}
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Seleccionar Bodega:</label>
                <Select 
                  value={assignDialog.selectedWarehouse} 
                  onValueChange={(v) => setAssignDialog(prev => ({ ...prev, selectedWarehouse: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona una bodega..." />
                  </SelectTrigger>
                  <SelectContent>
                    {warehouses.map(wh => (
                      <SelectItem key={wh.id} value={wh.id}>{wh.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialog({ open: false, location: null, selectedWarehouse: '' })}>
              Cancelar
            </Button>
            <Button 
              onClick={assignWarehouse} 
              disabled={!assignDialog.selectedWarehouse || syncing}
              className="bg-green-600 hover:bg-green-700"
            >
              {syncing ? 'Asignando...' : 'Asignar Bodega'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminLocalInventoriesPage;
